import { useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

// What a user may do with the event they are looking at:
// - admin ("Admin"): everything, including settings, vouchers and users
// - owner ("Tədbir sahibi"): reads statistics, tickets, ticket types, the seat
//   plan and the report, and hands out invitations and partner tickets;
//   selling and deleting stay with the admin
// - ticket control ("Bilet yoxlayan"): the ticket list, the seat plan, scanning
//
// The role is per event: the same person can run one event and only work the
// door at another, so every answer here is about the event in the URL.
export const EVENT_ADMIN = "event-admin";
export const EVENT_VIEWER = "event-viewer";
export const TICKET_CONTROL = "ticket-control";

export const ROLES = [
  { value: EVENT_ADMIN, label: "Admin", description: "Tam idarəetmə: parametrlər, voucherlar, istifadəçilər" },
  { value: EVENT_VIEWER, label: "Tədbir sahibi", description: "Statistika, biletlər, hesabatlar; dəvətnamə və əməkdaşlıq bileti yaza bilər" },
  { value: TICKET_CONTROL, label: "Bilet yoxlayan", description: "Biletlərin siyahısı, yer planı və bilet yoxlama" },
];

const ASSIGNABLE_ROLES = ROLES.map((item) => item.value);

export const roleLabel = (role) => ROLES.find((item) => item.value === role)?.label || role;

// What the organizer may hand out themselves; the rest is the admin's
export const OWNER_TICKET_TYPES = ["invitation", "cooperative"];

const OBJECT_ID = /^[0-9a-fA-F]{24}$/;

// /dashboard/<eventId>/… — the pages that are not about one event (the event
// list, create-event) have nothing here
export const eventIdFromPath = (pathname) => {
  const candidate = (pathname || "").split("/")[2];
  return OBJECT_ID.test(candidate || "") ? candidate : null;
};

export const hasEvent = (decoded, eventId) =>
  (decoded?.eventIds || []).map(String).includes(String(eventId));

// The role written for this event; accounts and tokens from before per-event
// roles existed carry only `type`, which stands as their default.
export const roleFor = (decoded, eventId) => {
  if (!eventId || !hasEvent(decoded, eventId)) return null;
  const own = (decoded?.eventRoles || []).find((item) => String(item.eventId) === String(eventId));
  return own?.role || decoded?.type || null;
};

const anyEventRole = (decoded, roles) => {
  const eventIds = decoded?.eventIds || [];
  if (!eventIds.length) return roles.includes(decoded?.type);
  return eventIds.some((eventId) => roles.includes(roleFor(decoded, eventId)));
};

// Whether this account holds one of these roles on any of its events, for the
// pages that stand outside a single event
export const hasRoleAnywhere = (decoded, roles) => anyEventRole(decoded, roles);

// Away from a single event — creating one, the event list: someone who runs at
// least one event, or an admin account with none yet
export const isAdminAnywhere = (decoded) => anyEventRole(decoded, [EVENT_ADMIN]);

// Anyone who belongs in the dashboard at all, whatever their role is
export const hasAnyEventRole = (decoded) => anyEventRole(decoded, ASSIGNABLE_ROLES);

export const decodeAuth = () => {
  const auth = localStorage.getItem("auth");
  try {
    return auth ? jwtDecode(auth) : null;
  } catch (error) {
    return null;
  }
};

// Pass an eventId where it is already in hand; otherwise it comes from the URL
const useRole = (eventIdArg) => {
  const location = useLocation();
  const decoded = decodeAuth();
  const eventId = eventIdArg || eventIdFromPath(location?.pathname);
  const role = roleFor(decoded, eventId);
  // off a single event's pages, "admin" means an admin of some event
  const isAdmin = eventId ? role === EVENT_ADMIN : isAdminAnywhere(decoded);

  return {
    user: decoded,
    eventId,
    role,
    isAdmin,
    isViewer: role === EVENT_VIEWER,
    isTicketControl: role === TICKET_CONTROL,
    // everyone who may look at the organizer's numbers
    seesStats: role === EVENT_ADMIN || role === EVENT_VIEWER,
    // the admin writes any ticket; the organizer only their own two kinds
    canIssueTickets: role === EVENT_ADMIN || role === EVENT_VIEWER,
    canDeleteTickets: role === EVENT_ADMIN,
    canEditTicket: (ticket) =>
      role === EVENT_ADMIN || (role === EVENT_VIEWER && OWNER_TICKET_TYPES.includes(ticket?.paidBy)),
  };
};

export default useRole;
